import { computed, onBeforeUnmount, ref, } from '@vue/composition-api';
import { createMentionUserFn } from '@/views/Brand/Applications/Report/_utils/mention-user';
import { useMessageFiles } from '@/views/Brand/Applications/Report/_composables/use-message-files';
import { destroyPlaceResourcesStore, usePlaceResourcesStore, } from '@/store/place-resources';
import { destroyObjectResourcesStore, useObjectResourcesStore, } from '@/store/object-resources';
import { isMessageDataEmpty } from '@/views/Brand/Applications/Report/_utils/message';
import { addReportMessage } from '@/api/requests/reports';
import { mapId } from '@/util/common';
import { EMPTY_WYSIWYG_VALUE } from '@/components/WysiwygInput/wysiwyg.constants';
import Vue from 'vue';
import { useAuthUser, useBrand } from '@/composables';
export const useReportChatMessage = (props, ctx) => {
    const { reportId, messages, validateActionRequiredUser, requireActionUser, disableActionRequiredMode, reportMessage, } = props;
    const message = ref('');
    const selectedOffer = ref(null);
    const showOfferModal = ref(false);
    const showAddAssetsBottomMenu = ref(false);
    const fileTokens = ref([]);
    const { brand } = useBrand();
    const { isObjektoAdmin, isEmployee } = useAuthUser();
    const canAddOffer = computed(() => isObjektoAdmin.value || isEmployee.value);
    const generateOfferHtml = (offer) => {
        return `<span class="embedded-offer" data-offer-id="${offer.id}" data-offer-type="${offer.offerType}" data-offer-title="${offer.title}" data-offer-internal-number="${offer.internalNumber || ''}" data-offer-created-at="${offer.createdAt}"></span>`.trim();
    };
    const selectOffer = (offer) => {
        selectedOffer.value = offer;
        showOfferModal.value = false;
        showAddAssetsBottomMenu.value = false;
    };
    const getMentionSuggestions = createMentionUserFn({
        accessToBrandId: brand.value.id,
    });
    const { files, handleAddFile, handleRemoveFile, getFileTokens, uploadingFiles, clearUploadedTokens, clearFiles, } = useMessageFiles(ctx, reportMessage);
    const { resolvedPlaces, resetPlacesState } = usePlaceResourcesStore();
    const { resolvedObjects, resetObjectsState } = useObjectResourcesStore();
    resetPlacesState();
    resetObjectsState();
    const isMessageEmpty = computed(() => {
        return isMessageDataEmpty({
            message,
            files,
            places: resolvedPlaces,
            objects: resolvedObjects,
        });
    });
    const setFileTokens = (tokens) => (fileTokens.value = tokens);
    const sendingInProgress = ref(false);
    const handleMessageSend = async () => {
        if (!validateActionRequiredUser.value) {
            return ctx.root.$toast.error(ctx.root.$t('report.missing-action-required-user'));
        }
        sendingInProgress.value = true;
        let messageWithOffer = message.value;
        if (selectedOffer.value) {
            const offerHtml = generateOfferHtml(selectedOffer.value);
            messageWithOffer = message.value + offerHtml;
        }
        const createdMessage = await addReportMessage({
            reportId: reportId.value,
            message: messageWithOffer,
            fileTokens: fileTokens.value,
            placeIds: mapId(resolvedPlaces.value),
            objectIds: mapId(resolvedObjects.value),
            ...(requireActionUser.value && {
                requiredActionUserId: requireActionUser.value.id,
            }),
        });
        disableActionRequiredMode();
        clearUploadedTokens();
        messages.value = [...messages.value, createdMessage];
        message.value = EMPTY_WYSIWYG_VALUE;
        selectedOffer.value = null;
        showOfferModal.value = false;
        clearFiles();
        resetPlacesState();
        resetObjectsState();
        sendingInProgress.value = false;
        requireActionUser.value = null;
    };
    const onMessageUpdated = (updatedMessage) => {
        const updatedMessageIndex = messages.value.findIndex(({ id }) => id === updatedMessage.id);
        Vue.set(messages.value, updatedMessageIndex, updatedMessage);
    };
    const onMessageDeleted = (deletedMessageId) => {
        messages.value = messages.value.filter(({ id }) => id !== deletedMessageId);
    };
    onBeforeUnmount(() => {
        destroyPlaceResourcesStore();
        destroyObjectResourcesStore();
    });
    const handleOfferClick = (offerId, offerType) => {
        const { id: brandId } = ctx.root.$route.params;
        let route = '';
        switch (offerType) {
            case 'goods':
                route = `/brand/${brandId}/offers/goods/${offerId}/details`;
                break;
            case 'objects':
                route = `/brand/${brandId}/offers/${offerId}/details`;
                break;
            default:
                route = `/brand/${brandId}/offers/${offerId}/details`;
        }
        window.open(route, '_blank');
    };
    return {
        message,
        showAddAssetsBottomMenu,
        selectedOffer,
        showOfferModal,
        isMessageEmpty,
        getMentionSuggestions,
        handleMessageSend,
        onMessageUpdated,
        onMessageDeleted,
        files,
        handleAddFile,
        handleRemoveFile,
        canAddOffer,
        selectOffer,
        sendingInProgress,
        getFileTokens,
        uploadingFiles,
        setFileTokens,
        handleOfferClick,
    };
};
